import React, { useEffect, useState, useCallback } from 'react';
import {
  Brain, Target, GitMerge, Activity, BarChart3, Layers,
  RefreshCw, AlertCircle, CheckCircle2, Clock,
} from 'lucide-react';

/**
 * IntelligencePage — unified admin surface for all six intelligence engines.
 *
 * Each engine has its own card that fetches from the existing intelligence:*
 * IPC channels (no new server code). Cards degrade gracefully when an
 * endpoint returns a permission denial or the feature flag is off — the UI
 * always renders, never blocks.
 *
 * Channels surfaced:
 *   Engine #1 (stale-lead)   intelligence:model:status (model_name='stale_lead_hazard_v1')
 *   Engine #2 (bandit)        intelligence:outreach:list  +  intelligence:outreach:stats
 *   Engine #3 (linkage)       intelligence:linkage:candidates  (top pending)
 *   Engine #4 (NBA)           intelligence:nba:queue   (rep-facing; admin sees own queue)
 *   Engine #5 (Bayesian)      intelligence:model:status (model_name='bayesian_state_v1')
 *   Engine #6 (meta-engine)   intelligence:model:evaluations
 *
 * Permission: page-level guard is model_eval:read (sysadmin + admin). Individual
 * sections call their own channels — if a channel denies, the section shows a
 * "permission denied" badge but the rest of the page still renders.
 */

// ── IPC wrapper that works in both Electron and web ────────────────────────
async function ipc(channel, ...args) {
  if (typeof window !== 'undefined' && window.zignagenixAPI?.invoke) {
    return window.zignagenixAPI.invoke(channel, ...args);
  }
  // Web fallback — direct fetch.
  try {
    const res = await fetch('/api/ipc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel, args }),
    });
    return await res.json();
  } catch (err) {
    return { error: true, code: 'NETWORK', message: err.message };
  }
}

// ── Shared card chrome ─────────────────────────────────────────────────────
function EngineCard({ index, title, icon: Icon, flagName, status, children, error }) {
  const flagOn = status === 'flag_on';
  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E8ECF1',
      borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
        <div style={{
          background: '#E6F0F3', color: '#0F4C5C', borderRadius: 8,
          width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 13,
        }}>{index}</div>
        {Icon ? <Icon size={20} color="#1B6A7E" /> : null}
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1f2937' }}>{title}</h3>
          {flagName && (
            <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'monospace', marginTop: 2 }}>
              {flagName}
            </div>
          )}
        </div>
        <StatusBadge status={status} />
      </div>
      {error && (
        <div style={{
          background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA',
          padding: '0.5rem 0.75rem', borderRadius: 8, fontSize: 12, marginBottom: '0.75rem',
        }}>{error}</div>
      )}
      <div style={{ opacity: flagOn ? 1 : 0.6 }}>{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    flag_on:        { bg: '#15803D', label: 'FLAG ON',       icon: CheckCircle2 },
    flag_off:       { bg: '#6B7280', label: 'FLAG OFF',      icon: Clock },
    permission_denied: { bg: '#B45309', label: 'NO PERMISSION', icon: AlertCircle },
    error:          { bg: '#991B1B', label: 'ERROR',         icon: AlertCircle },
    loading:        { bg: '#1B6A7E', label: 'LOADING…',      icon: RefreshCw },
  };
  const s = map[status] || map.loading;
  return (
    <div style={{
      background: s.bg, color: 'white', fontSize: 10, fontWeight: 700,
      padding: '3px 8px', borderRadius: 999, letterSpacing: 0.5,
      display: 'flex', alignItems: 'center', gap: 4,
    }}>
      {s.icon ? <s.icon size={11} /> : null}{s.label}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#0F4C5C', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StatRow({ children }) {
  return (
    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
      {children}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function classifyResult(result) {
  if (!result) return { status: 'error', error: 'No response' };
  if (result.error) {
    if (/PERMISSION|ACCESS|FORBIDDEN/i.test(result.code || '')) {
      return { status: 'permission_denied', error: result.message };
    }
    return { status: 'error', error: result.message || result.code };
  }
  if (result.available === false) {
    return { status: 'flag_off', error: null };
  }
  return { status: 'flag_on', error: null };
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

// ── Engine-specific section bodies ─────────────────────────────────────────

function StaleLeadSection({ data, error }) {
  if (!data || data.available === false) {
    return <div style={{ color: '#6B7280', fontSize: 13 }}>
      No active model artifact. Seed via the GHA workflow or run the trainer locally.
    </div>;
  }
  return (
    <>
      <StatRow>
        <Stat label="Model version" value={data.model_version || '—'} sub={fmtDate(data.trained_at)} />
        <Stat label="Trained by"    value={data.trained_by || '—'} />
        <Stat label="Total versions" value={data.total_versions ?? '—'} />
      </StatRow>
      <div style={{ fontSize: 12, color: '#6B7280', marginTop: '0.5rem' }}>
        Features: {(data.features || []).join(', ')}
      </div>
    </>
  );
}

function OutreachBanditSection({ stats, error }) {
  if (!stats || stats.available === false) {
    return <div style={{ color: '#6B7280', fontSize: 13 }}>
      No experiment data yet. Migration 073 seeds the default experiment but no assignments have been made.
    </div>;
  }
  const a = stats.assignments || {};
  return (
    <>
      <StatRow>
        <Stat label="Experiment" value={stats.experiment || '—'} />
        <Stat label="Pending"    value={a.pending ?? 0} />
        <Stat label="Observed"   value={a.observed ?? 0} />
        <Stat label="Successes"  value={a.successes ?? 0} />
        <Stat label="Failures"   value={a.failures ?? 0} />
      </StatRow>
      {Array.isArray(stats.arms) && stats.arms.length > 0 && (
        <table style={{ width: '100%', fontSize: 12, marginTop: '0.5rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E8ECF1' }}>
              <th style={{ padding: '4px 8px' }}>Cohort</th>
              <th style={{ padding: '4px 8px' }}>Arm</th>
              <th style={{ padding: '4px 8px' }}>Pulls</th>
              <th style={{ padding: '4px 8px' }}>Posterior mean</th>
            </tr>
          </thead>
          <tbody>
            {stats.arms.slice(0, 12).map((arm, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{arm.cohort_key}</td>
                <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{arm.arm_key}</td>
                <td style={{ padding: '4px 8px' }}>{arm.total_pulls}</td>
                <td style={{ padding: '4px 8px' }}>{(arm.posterior_mean * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function LinkageSection({ data, error }) {
  if (!data || !Array.isArray(data.candidates)) {
    return <div style={{ color: '#6B7280', fontSize: 13 }}>
      No pending duplicate candidates. Run the record-linkage workflow to score new pairs.
    </div>;
  }
  return (
    <>
      <StatRow>
        <Stat label="Pending" value={data.candidates.length} />
        <Stat label="Min score" value={(data.min_score ?? 0).toFixed(2)} />
      </StatRow>
      {data.candidates.length > 0 && (
        <div style={{ marginTop: '0.5rem', fontSize: 13 }}>
          <div style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }}>Top pending pairs</div>
          {data.candidates.slice(0, 5).map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 12, padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ fontWeight: 700, color: '#0F4C5C', width: 50 }}>
                {(c.match_score * 100).toFixed(0)}%
              </div>
              <div style={{ flex: 1 }}>
                {c.a_first_name || '?'} {c.a_last_name || '?'} &nbsp;⇄&nbsp; {c.b_first_name || '?'} {c.b_last_name || '?'}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function NbaSection({ data, error }) {
  if (!data || data.available === false) {
    return <div style={{ color: '#6B7280', fontSize: 13 }}>
      NBA flag off (FEATURE_INTEL_NBA_V1=false). Flip to start surfacing recommendations.
    </div>;
  }
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <>
      <StatRow>
        <Stat label="Surfaced (your queue)" value={items.length} />
        <Stat label="Weights"
              value={data.weights
                ? `${(data.weights.w_hazard * 100).toFixed(0)}/${(data.weights.w_score * 100).toFixed(0)}/${(data.weights.w_value * 100).toFixed(0)}`
                : '—'}
              sub="hazard / score / value" />
        <Stat label="Generated" value={data.generated_at ? fmtDate(data.generated_at) : '—'} />
      </StatRow>
    </>
  );
}

function BayesianStateSection({ data, error }) {
  if (!data || data.available === false) {
    return <div style={{ color: '#6B7280', fontSize: 13 }}>
      No active artifact metadata. Engine ships dark until Dr. Emad sign-off (roadmap §3.5).
    </div>;
  }
  return (
    <>
      <StatRow>
        <Stat label="Model version" value={data.model_version || '—'} sub={fmtDate(data.trained_at)} />
        <Stat label="Schema version" value={data.metadata?.schema_version ?? 1} />
      </StatRow>
      <div style={{ fontSize: 12, color: '#6B7280', marginTop: '0.5rem' }}>
        Latent dimensions: {(data.features || []).join(', ')}
      </div>
    </>
  );
}

function MetaEngineSection({ data, error }) {
  const rows = Array.isArray(data?.evaluations) ? data.evaluations : [];
  if (rows.length === 0) {
    return <div style={{ color: '#6B7280', fontSize: 13 }}>
      No evaluations yet. Weekly cron runs Tuesdays 09:00 UTC.
    </div>;
  }
  return (
    <>
      <StatRow>
        <Stat label="Recent evaluations" value={rows.length} />
        <Stat label="Promoted" value={rows.filter(r => r.decision === 'promoted').length} />
        <Stat label="Rejected" value={rows.filter(r => r.decision === 'rejected').length} />
        <Stat label="Shadow"   value={rows.filter(r => r.decision === 'shadow').length} />
      </StatRow>
      <table style={{ width: '100%', fontSize: 12, marginTop: '0.5rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E8ECF1' }}>
            <th style={{ padding: '4px 8px' }}>Model</th>
            <th style={{ padding: '4px 8px' }}>Method</th>
            <th style={{ padding: '4px 8px' }}>Score</th>
            <th style={{ padding: '4px 8px' }}>Dataset</th>
            <th style={{ padding: '4px 8px' }}>Decision</th>
            <th style={{ padding: '4px 8px' }}>Evaluated</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 10).map(r => (
            <tr key={r.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{r.model_name}</td>
              <td style={{ padding: '4px 8px' }}>{r.evaluation_method}</td>
              <td style={{ padding: '4px 8px' }}>{Number(r.evaluation_score).toFixed(4)}</td>
              <td style={{ padding: '4px 8px' }}>{r.dataset_size}</td>
              <td style={{ padding: '4px 8px', fontWeight: 600,
                  color: r.decision === 'promoted' ? '#15803D'
                       : r.decision === 'rejected' ? '#991B1B'
                       : r.decision === 'shadow' ? '#1B6A7E' : '#6B7280' }}>
                {r.decision}
              </td>
              <td style={{ padding: '4px 8px', color: '#6B7280' }}>{fmtDate(r.evaluated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function IntelligencePage({ user }) {
  const [state, setState] = useState({
    stale: { status: 'loading', data: null, error: null },
    bandit: { status: 'loading', data: null, error: null },
    linkage: { status: 'loading', data: null, error: null },
    nba: { status: 'loading', data: null, error: null },
    bayes: { status: 'loading', data: null, error: null },
    meta: { status: 'loading', data: null, error: null },
  });
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const refresh = useCallback(async () => {
    setLastFetchedAt(null);
    const [stale, bandit, linkage, nba, bayes, meta] = await Promise.all([
      ipc('intelligence:model:status', 'stale_lead_hazard_v1'),
      ipc('intelligence:outreach:stats', 'sequence_step_send_time_v1'),
      ipc('intelligence:linkage:candidates', { status: 'pending', limit: 10 }),
      ipc('intelligence:nba:queue', { limit: 25 }),
      ipc('intelligence:model:status', 'bayesian_state_v1'),
      ipc('intelligence:model:evaluations', { limit: 50 }),
    ]);

    setState({
      stale:   { ...classifyResult(stale),   data: stale },
      bandit:  { ...classifyResult(bandit),  data: bandit },
      linkage: { ...classifyResult(linkage), data: linkage },
      nba:     { ...classifyResult(nba),     data: nba },
      bayes:   { ...classifyResult(bayes),   data: bayes },
      meta:    { ...classifyResult(meta),    data: meta },
    });
    setLastFetchedAt(new Date());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: 1400, margin: '0 auto' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <Brain size={28} color="#0F4C5C" />
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0F4C5C' }}>
            Intelligence Engines
          </h1>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            Six decision engines + continuous-learning validation.
            {lastFetchedAt && (
              <span style={{ marginLeft: 12, color: '#9CA3AF' }}>
                Refreshed {lastFetchedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={refresh}
          data-testid="intel-refresh"
          style={{
            background: '#1B6A7E', color: 'white', border: 'none',
            padding: '0.5rem 0.9rem', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ── Engine cards ── */}
      <EngineCard index="1" title="Stale-lead hazard (Cox PH)" icon={Activity}
                  flagName="FEATURE_INTEL_STALE_HAZARD"
                  status={state.stale.status} error={state.stale.error}>
        <StaleLeadSection data={state.stale.data} error={state.stale.error} />
      </EngineCard>

      <EngineCard index="2" title="Outreach bandit (Thompson sampling)" icon={Target}
                  flagName="FEATURE_INTEL_OUTREACH_BANDIT"
                  status={state.bandit.status} error={state.bandit.error}>
        <OutreachBanditSection stats={state.bandit.data} error={state.bandit.error} />
      </EngineCard>

      <EngineCard index="3" title="Record linkage (Fellegi-Sunter)" icon={GitMerge}
                  flagName="FEATURE_INTEL_RECORD_LINKAGE"
                  status={state.linkage.status} error={state.linkage.error}>
        <LinkageSection data={state.linkage.data} error={state.linkage.error} />
      </EngineCard>

      <EngineCard index="4" title="Next-best-action composition" icon={BarChart3}
                  flagName="FEATURE_INTEL_NBA_V1"
                  status={state.nba.status} error={state.nba.error}>
        <NbaSection data={state.nba.data} error={state.nba.error} />
      </EngineCard>

      <EngineCard index="5" title="Bayesian latent state (moonshot)" icon={Layers}
                  flagName="FEATURE_INTEL_BAYESIAN_STATE_V1"
                  status={state.bayes.status} error={state.bayes.error}>
        <BayesianStateSection data={state.bayes.data} error={state.bayes.error} />
      </EngineCard>

      <EngineCard index="6" title="Continuous-learning meta-engine" icon={Brain}
                  flagName="(always on — weekly cron)"
                  status={state.meta.status} error={state.meta.error}>
        <MetaEngineSection data={state.meta.data} error={state.meta.error} />
      </EngineCard>
    </div>
  );
}
